/**
 * @file pages/VerifyEmailPage.js
 * @description Landing page for the email-verification link (Amendment 11).
 *
 * The link in the verification email is {APP_BASE_URL}/verify-email?token=...
 * On mount we POST the token to /auth/verify-email. On success the server logs
 * the user in (sets cookies) and we redirect into the app. On failure we show a
 * clear message and let them request a fresh link.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input, Button, useToast } from "../design-system";

function VerifyEmailPage() {
  const auth = useAuth();
  const toast = useToast();
  const [params] = useSearchParams();
  const token = params.get("token");

  // status: 'verifying' | 'success' | 'error' | 'missing'
  const [status, setStatus] = useState(token ? "verifying" : "missing");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  // Guard against double-invoke (React 18 StrictMode mounts effects twice in dev).
  const ranRef = useRef(false);

  useEffect(() => {
    if (!token || ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        await auth.verifyEmail(token);
        setStatus("success");
      } catch {
        setStatus("error");
      }
    })();
  }, [token, auth]);

  const handleResend = async () => {
    if (!resendEmail || !resendEmail.includes("@")) {
      toast.error("Enter the email you signed up with");
      return;
    }
    setResending(true);
    try {
      await auth.resendVerification(resendEmail);
      toast.success("If that email needs verification, a new link is on its way.");
    } catch (err) {
      toast.error(err.message || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  // Verified + logged in → straight into the app.
  if (status === "success") {
    toast.success("Email verified. You're signed in.");
    if (typeof window !== 'undefined') window.location.href = '/app/'; return null;
  }

  const wrap = { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "var(--space-6) var(--space-4)", backgroundColor: "var(--surface-page)" };
  const card = { width: "100%", maxWidth: "440px", textAlign: "center" };
  const h1 = { fontSize: "var(--text-h2)", fontWeight: "var(--weight-bold)", color: "var(--text-primary)", margin: "0 0 var(--space-3)", fontFamily: "var(--font-family)", letterSpacing: "var(--tracking-tight)" };
  const sub = { fontSize: "var(--text-body-md)", color: "var(--text-secondary)", margin: "0 0 var(--space-6)", fontFamily: "var(--font-family)" };

  if (status === "verifying") {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ fontSize: "48px", marginBottom: "var(--space-4)" }}>{"⏳"}</div>
          <h1 style={h1}>Verifying your email…</h1>
          <p style={sub}>One moment while we confirm your link.</p>
        </div>
      </div>
    );
  }

  // error or missing token — let them request a fresh link.
  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ fontSize: "48px", marginBottom: "var(--space-4)" }}>{"⚠️"}</div>
        <h1 style={h1}>{status === "missing" ? "No verification link found" : "This link didn't work"}</h1>
        <p style={sub}>
          {status === "missing"
            ? "Open the verification link from your email, or request a new one below."
            : "Your verification link is invalid or has expired. Enter your email to get a fresh one."}
        </p>
        <div style={{ marginBottom: "var(--space-4)", textAlign: "left" }}>
          <Input label="Email" type="email" size="lg" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="you@company.com" autoCapitalize="none" autoCorrect="off" autoComplete="email" />
        </div>
        <Button variant="primary" size="lg" fullWidth loading={resending} onClick={handleResend}>Send a new verification link</Button>
        <p style={{ textAlign: "center", marginTop: "var(--space-6)", fontSize: "var(--text-body-sm)", color: "var(--text-secondary)", fontFamily: "var(--font-family)" }}>
          <Link to="/login" style={{ color: "var(--color-primary)", fontWeight: "var(--weight-semibold)", textDecoration: "none" }}>Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
